<template>
  <v-row>
    <v-col cols="12" :lg="isQuotesPortalOrGuest && showActivity ? 9 : 12">
      <quote-loading v-if="loading || orderLoading" />

      <template v-if="!loading && quote && !orderLoading">
        <div
          class="d-flex justify-space-between flex-column flex-lg-row"
          :style="!$vuetify.display.mobile ? 'height: 36px' : ''"
        >
          <div class="d-flex flex-row">
            <show-categories-button v-if="!isQuotesPortal && !isGuest" class="mr-1" />
            <sf-icon-button
              v-else-if="isLoggedInQuotesPortal"
              :icon="mdiArrowLeftBoldOutline"
              @click="navigateToQuotesOverview"
            />
            <div class="d-flex flex-wrap flex-lg-nowrap align-center">
              <editable-heading
                v-if="quote.quoteStatus === QuoteStatus.DRAFT"
                class="mr-2"
                :value="quote.name"
                :label="$t('quotes.editTitle')"
                @input="updateTitle"
              />
              <sf-title
                v-else
                class="mr-2"
                :class="[
                  {
                    'text-disabled': quote.quote && quote.quote.isArchived,
                    'text-truncate': !$vuetify.display.mobile
                  }
                ]"
              >
                {{ quote.name }}
              </sf-title>
              <div class="d-flex flex-shrink-0 align-center">
                <sf-color-text
                  id="pw-quote-status"
                  :color="getQuoteStatusColor(quote.quoteStatus)"
                  class="text-center"
                >
                  {{ $t('quoteStatus.' + quote.quoteStatus) }}
                </sf-color-text>
                <sf-archived-chip v-if="quote.quote && quote.quote.isArchived" class="ml-2" />
              </div>
            </div>
          </div>

          <div class="d-flex flex-wrap justify-end flex-shrink-0">
            <delete-quote-request-button
              v-if="quote.quoteStatus === QuoteStatus.DRAFT"
              :id="quote.id"
            />
            <request-quote-action-button v-if="showRequestButton" class="ml-1" />
            <template v-if="isQuotesPortalOrGuest && quote.quoteStatus !== QuoteStatus.DRAFT">
              <sf-text-button
                v-if="isGuest && quote.quoteStatus !== QuoteStatus.REQUESTED"
                ref="printButton"
                class="text-medium-emphasis"
                :loading="printing"
                :disabled="isReadonly"
                @click="printQuote"
              >
                {{ $t('print') }}
              </sf-text-button>
              <rejection-dialog
                v-if="quote.quoteStatus === QuoteStatus.NEGOTIATION"
                guest-quote
                :rejecting="rejecting"
                :type="'QUOTE'"
                :disabled="isReadonly"
                @reject="rejectQuote"
              />
              <sf-button
                v-if="quote.quoteStatus === QuoteStatus.NEGOTIATION"
                ref="ct-checkout-button"
                :disabled="
                  isReadonly ||
                  noPositionSelected ||
                  !addressValidation.valid ||
                  (quote.quote && quote.quote.validDate && !dateNotOver(quote.quote.validDate)) ||
                  isDuplicateUser
                "
                :loading="ordering"
                @click="orderQuote"
              >
                {{ $t('quotes.acceptQuote') }}
              </sf-button>
            </template>
            <div class="ml-1">
              <quote-menu
                v-if="
                  !isGuest &&
                  quote.quoteStatus !== QuoteStatus.REQUESTED &&
                  quote.quoteStatus !== QuoteStatus.DRAFT
                "
              />
            </div>
            <sf-icon-button
              v-if="
                isQuotesPortalOrGuest &&
                quote.quoteStatus !== QuoteStatus.DRAFT &&
                (!showActivity || $vuetify.display.mobile)
              "
              id="pw-quote-activity-toggle"
              class="ml-1"
              :size="32"
              :icon="mdiMessageText"
              :aria-label="$t('quotes.showActivity')"
              @click="toggleActivity()"
            />
          </div>
        </div>
        <sf-alert-warning v-if="isDuplicateUser" class="mt-1">
          {{ $t('quotes.duplicateUserWarning') }}
        </sf-alert-warning>
        <sf-alert-warning
          v-if="quote.quote && quote.quote.validDate && !dateNotOver(quote.quote.validDate)"
          class="mt-1"
        >
          {{ $t('quotes.noLongerValid', [formatDate(quote.quote.validDate, false)]) }}
        </sf-alert-warning>
        <template v-if="quote.quote && !quoteRequest && !quote.orderId">
          <v-divider class="mt-2 mb-1" />
          <quote-details :quote="quote.quote" :quote-status="quote.quoteStatus" />
        </template>
        <template v-else>
          <v-tabs v-model="tab" class="mt-1" show-arrows bg-color="transparent" color="primary">
            <v-tab v-if="quoteRequest" :value="QuoteTabs.REQUEST">{{ $t('quotes.request') }}</v-tab>
            <v-tab v-if="!isWaitingForApproval" :value="QuoteTabs.QUOTE">
              {{ $t('quotes.quote') }}
            </v-tab>
            <v-tab v-if="!isQuotesPortal && quote.orderId" :value="QuoteTabs.ORDER">
              {{ $t('order.order') }}
            </v-tab>
            <v-tab v-if="quote.orderId" :value="QuoteTabs.DOCUMENTS">
              {{ $t('order.documents') }}
            </v-tab>
          </v-tabs>
          <v-divider />
          <v-window v-model="tab">
            <v-window-item v-if="quoteRequest" class="mx-half" :value="QuoteTabs.REQUEST">
              <request-details />
            </v-window-item>
            <v-window-item class="mx-half" :value="QuoteTabs.QUOTE">
              <quote-details
                v-if="quote.quote"
                :quote="quote.quote"
                :quote-status="quote.quoteStatus"
              />
              <list-empty v-else :icon="mdiHandshake" :message="$t('quotes.noQuoteEmptySpace')" />
            </v-window-item>
            <v-window-item
              v-if="!isQuotesPortal && quote.orderId"
              class="mx-half"
              :value="QuoteTabs.ORDER"
            >
              <order-details class="mt-1" />
            </v-window-item>
            <v-window-item v-if="quote.orderId" class="mx-half" :value="QuoteTabs.DOCUMENTS">
              <order-documents class="mt-1" :order-id-prop="quote.orderId" />
            </v-window-item>
          </v-window>
        </template>
      </template>
    </v-col>
    <template
      v-if="
        !loading &&
        quote &&
        quote.quoteStatus !== QuoteStatus.DRAFT &&
        isQuotesPortalOrGuest &&
        !orderLoading
      "
    >
      <mobile-side-menu
        v-if="$vuetify.display.mobile"
        :show="showActivity"
        @close="toggleActivity()"
      >
        <quote-activity :quote-id="quote.id" @close="toggleActivity()" />
      </mobile-side-menu>

      <v-col v-if="!$vuetify.display.mobile" v-show="showActivity" id="pw-quote-activity" cols="3">
        <quote-activity :quote-id="quote.id" @close="toggleActivity()" />
      </v-col>
    </template>
  </v-row>
</template>

<script setup lang="ts">
import MobileSideMenu from '@/components/MobileSideMenu.vue'
import RejectionDialog from '@/components/RejectionDialog.vue'
import ShowCategoriesButton from '@/components/ShowCategoriesButton.vue'
import SfAlertWarning from '@/components/alerts/SfAlertWarning.vue'
import SfButton from '@/components/button/SfButton.vue'
import SfIconButton from '@/components/button/SfIconButton.vue'
import SfTextButton from '@/components/button/SfTextButton.vue'
import SfArchivedChip from '@/components/chips/SfArchivedChip.vue'
import EditableHeading from '@/components/input/EditableHeading.vue'
import ListEmpty from '@/components/list/ListEmpty.vue'
import SfColorText from '@/components/text/SfColorText.vue'
import SfTitle from '@/components/text/SfTitle.vue'
import { isGuest, isQuotesPortal, isQuotesPortalOrGuest, scrollToTop, useTitle } from '@/helpers'
import useAddressValidation from '@/modules/address/useAddressValidation'
import useAuthentication from '@/modules/authentication/useAuthentication'
import OrderDetails from '@/modules/order/components/OrderDetails.vue'
import OrderDocuments from '@/modules/order/components/OrderDocuments.vue'
import useOrder from '@/modules/order/useOrder'
import DeleteQuoteRequestButton from '@/modules/quote/components/DeleteQuoteRequestButton.vue'
import QuoteActivity from '@/modules/quote/components/QuoteActivity.vue'
import QuoteDetails from '@/modules/quote/components/QuoteDetails.vue'
import QuoteLoading from '@/modules/quote/components/QuoteLoading.vue'
import QuoteMenu from '@/modules/quote/components/QuoteMenu.vue'
import RequestDetails from '@/modules/quote/components/RequestDetails.vue'
import RequestQuoteActionButton from '@/modules/quote/components/RequestQuoteActionButton.vue'
import { getQuoteStatusColor } from '@/modules/quote/helpers'
import useQuote from '@/modules/quote/useQuote'
import useQuoteRequest from '@/modules/quote/useQuoteRequest'
import QuoteEvent, { GuestQuoteEvent } from '@/modules/tracking/events/quote'
import { trackEvent } from '@/modules/tracking/useTracking'
import useGuestUser from '@/modules/user/useGuestUser'
import useOwnUser from '@/modules/user/useOwnUser'
import vuetify from '@/plugins/vuetify'
import router from '@/router'
import { QuoteTabs } from '@/types'
import { AddressType, ApprovalRequest, ApprovalRequestStatus, QuoteStatus } from '@/types/index'
import { dateNotOver, formatDate } from '@/utils/timeFormatHelpers'
import { mdiArrowLeftBoldOutline, mdiHandshake, mdiMessageText } from '@mdi/js'
import { useToggle } from '@vueuse/core'
import { storeToRefs } from 'pinia'
import { computed, onBeforeUnmount, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute } from 'vue-router'

const currentRoute = useRoute()
useQuote().getQuote(currentRoute.params.id as string)
onBeforeUnmount(() => useQuote().closeQuoteUpdateStream())

const { quote, loading, rejecting, printing, ordering } = storeToRefs(useQuote())
const { quoteRequest } = storeToRefs(useQuoteRequest())
const { order, loading: orderLoading } = storeToRefs(useOrder())
const { ownUser } = storeToRefs(useOwnUser())
const tab = ref<QuoteTabs>(QuoteTabs.REQUEST)
const [showActivity, toggleActivity] = useToggle(!vuetify.display.mobile.value)
const isReadonly = computed(() => useAuthentication().state.value.isReadonly)

const { t } = useI18n()
const pageTitle = computed(() => {
  let quoteNumber = quote.value?.name
  if (quote.value?.quote) {
    const quoteVersionSuffix =
      quote.value.quote.quoteVersion !== '1' ? '-V' + quote.value.quote.quoteVersion : ''
    quoteNumber = quote.value.quote.quoteNumber + quoteVersionSuffix
  }
  return `${t('quotes.quote')} ${quoteNumber}`
})
useTitle(pageTitle)

watch(currentRoute, () => {
  scrollToTop(undefined)
  useQuote().getQuote(currentRoute.params.id as string)
})

const isLoggedInQuotesPortal = computed(() => !isGuest.value && isQuotesPortal.value)

const showRequestButton = computed(() => {
  return (
    tab.value === QuoteTabs.REQUEST &&
    quote.value &&
    quote.value.quoteStatus === QuoteStatus.DRAFT &&
    !quote.value.quote &&
    quoteRequest.value &&
    quoteRequest.value.items.length > 0
  )
})

watch(loading, () => {
  if (!loading.value && quote.value) {
    tab.value = quote.value.quote ? QuoteTabs.QUOTE : QuoteTabs.REQUEST
    if (!isQuotesPortal.value && quote.value.orderId) {
      useOrder().getOrder(quote.value.orderId)
    }
  }
})

const addressValidation = useAddressValidation()[AddressType.DELIVERY]

const updateTitle = (name: string) => {
  if (quote.value) {
    quote.value.name = name
    trackEvent(QuoteEvent.EditRequestTitle)
    useQuoteRequest().updateQuoteRequest(quote.value.id, { name })
  }
}

const noPositionSelected = computed(() => {
  return !quote.value?.quote?.items.find(
    (item) => (item.isOptional && item.isPickedOptional) || !item.isOptional
  )
})

const orderQuote = () => {
  if (quote.value) {
    useQuote().orderPlatformQuoteAsGuest(quote.value.id)
    trackEvent(GuestQuoteEvent.Accept)
  }
}

const rejectQuote = (rejectionComment: string) => {
  if (quote.value) {
    useQuote().rejectQuote(quote.value.id, rejectionComment)

    if (isGuest.value) {
      trackEvent(GuestQuoteEvent.Reject)
    } else {
      trackEvent(QuoteEvent.Decline)
    }
  }
}

const printQuote = () => {
  if (quote.value) {
    useQuote().printQuote(quote.value.id)

    if (isGuest.value) {
      trackEvent(GuestQuoteEvent.Print)
    } else {
      trackEvent(QuoteEvent.Download.Quote)
    }
  }
}

const navigateToQuotesOverview = () => {
  router.push('/quotes')
}

const isWaitingForApproval = computed(() => {
  const approvalRequest: ApprovalRequest | undefined = order.value?.approvalRequest || undefined
  return (
    approvalRequest?.status === ApprovalRequestStatus.PENDING &&
    approvalRequest.assignees.find((approver) => approver.id === ownUser.value.id)
  )
})

watch(isWaitingForApproval, () => {
  if (isWaitingForApproval.value) {
    tab.value = QuoteTabs.ORDER
  }
})

const isDuplicateUser = computed(() => {
  return isQuotesPortal && useGuestUser().guestUser.isDuplicateUser
})
</script>
